/**
 * The register's filter row: a name search, a single-select view over
 * `segmentOf`'s three classes (plus "Ativas", funded and waiting combined,
 * and "Todas", every row), and a cross-cutting open-request toggle that ANDs
 * with the selected view. Every option always shows its own count, whatever
 * is currently selected or searched -- an unbounded list must never shrink
 * silently with no way to see what it hid.
 */

import type { ReactNode } from 'react';
import { Inbox, Search } from 'lucide-react';

import { getLocale } from '../../i18n';
import { integer } from '../../i18n/format';
import { FilterBar as SearchLandmark } from '../../chrome/table';
import { Chip, chipVariants } from '../../ui/chip';
import { Input } from '../../ui/input';
import { Toggle } from '../../ui/toggle';
import { SEGMENTED_ITEM_CLASSNAME, ToggleGroup, ToggleGroupItem } from '../../ui/toggle-group';
import { cn } from '../../ui/utils';
import { t } from './messages';
import type { Segment, SegmentCounts } from './segments';

export type RegisterView = 'active' | Segment | 'all';

/** Whether a row's `segmentOf` class belongs to the selected view -- "Ativas"
 *  is funded or waiting, "Todas" is every class, and every other view is
 *  exactly one class. Adds no classification of its own; `segmentOf` still
 *  decides which of the three classes a row is in. */
export function matchesView(view: RegisterView, segment: Segment): boolean {
  if (view === 'all') return true;
  if (view === 'active') return segment === 'funded' || segment === 'waiting';
  return view === segment;
}

const VIEWS: {
  value: RegisterView;
  labelKey: 'view.active' | 'view.funded' | 'view.waiting' | 'view.inert' | 'view.all';
}[] = [
  { value: 'active', labelKey: 'view.active' },
  { value: 'funded', labelKey: 'view.funded' },
  { value: 'waiting', labelKey: 'view.waiting' },
  { value: 'inert', labelKey: 'view.inert' },
  { value: 'all', labelKey: 'view.all' },
];

function countFor(view: RegisterView, counts: SegmentCounts, total: number): number {
  if (view === 'all') return total;
  if (view === 'active') return counts.funded + counts.waiting;
  return counts[view];
}

export function FilterBar(props: {
  search: string;
  onSearchChange: (value: string) => void;
  view: RegisterView;
  onViewChange: (view: RegisterView) => void;
  counts: SegmentCounts;
  total: number;
  openRequestOnly: boolean;
  onOpenRequestOnlyChange: (value: boolean) => void;
  openRequestCount: number;
}): ReactNode {
  const locale = getLocale();
  return (
    // The open-request toggle is a filter that ANDs with the others, not a
    // page-level action, so it rides in `children`'s own flex-wrap alongside
    // search and the segments -- `actions`' `shrink-0` sibling never wraps,
    // and at a width too narrow for both columns it pushed the whole row
    // past the viewport instead of dropping to its own line.
    <SearchLandmark label={t('filter.label')}>
      <div className="relative">
        <Search
          aria-hidden
          className="text-muted-foreground pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2"
        />
        <Input
          type="search"
          aria-label={t('search.label')}
          placeholder={t('search.placeholder')}
          value={props.search}
          onChange={(event) => props.onSearchChange(event.target.value)}
          className="pl-8 md:w-65"
        />
      </div>
      {/* `min-w-0`: a flex item's default `min-width: auto` refuses to
          shrink below its content, which would leave `overflow-x-auto`
          nothing to ever scroll and the whole row overflowing the
          viewport instead of this one control. */}
      <div className="min-w-0 overflow-x-auto">
        <ToggleGroup
          type="single"
          spacing={1}
          aria-label={t('view.groupLabel')}
          value={props.view}
          // Radix reports an empty string when the selected item is pressed
          // again; a single-select view always has one option chosen.
          onValueChange={(next) => {
            if (next !== '') props.onViewChange(next as RegisterView);
          }}
          className="bg-secondary rounded-pill p-1"
        >
          {VIEWS.map((view) => (
            <ToggleGroupItem
              key={view.value}
              value={view.value}
              className={SEGMENTED_ITEM_CLASSNAME}
            >
              {t('withCount', {
                label: t(view.labelKey),
                count: integer(countFor(view.value, props.counts, props.total), locale),
              })}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      </div>
      <Toggle
        pressed={props.openRequestOnly}
        onPressedChange={props.onOpenRequestOnlyChange}
        className={cn(
          chipVariants({ tone: 'outline' }),
          'h-auto shrink-0 gap-1.5 data-[state=on]:border-transparent data-[state=on]:bg-primary-quiet data-[state=on]:text-primary-quiet-foreground',
        )}
      >
        <Inbox aria-hidden className="size-3.25" />
        {t('withCount', {
          label: t('openRequestFilter.label'),
          count: integer(props.openRequestCount, locale),
        })}
      </Toggle>
    </SearchLandmark>
  );
}

/** The Pedidos cell: every row carries one, positive when a request is open
 *  and neutral otherwise, so the column never renders a blank cell that
 *  would leave its row a different height from its neighbours. */
export function OpenRequestChip(props: { open: boolean }): ReactNode {
  return props.open ? (
    <Chip data-testid="open-request" tone="positive" size="sm">
      {t('openRequest.some')}
    </Chip>
  ) : (
    <Chip tone="neutral" size="sm">
      {t('openRequest.none')}
    </Chip>
  );
}
